<!-- 基础认证 -->
<template>
    <div class="basicAuth">
        <div class="title-box">
            <a-button class="back-btn" @click="goBack">
                <template #icon>
                    <icon-left/>
                </template>
            </a-button>
            <span>{{ $t('personal.level_one_basic_verification') }}</span>
        </div>
        <div class="basic-body">
            <!-- 加载状态 -->
            <div v-if="isLoading" class="loading-container">
                <a-spin size="large" />
                <p>{{ $t('common.loading') }}</p>
            </div>
            
            <!-- 认证状态显示 -->
            <div v-else-if="authStatus !== null" class="auth-status-container">
                <div class="status-card">
                    <div v-if="authStatus === 0" class="status-pending">
                        <div class="status-icon">
                            <icon-clock-circle size="64" style="color: #ff9e2d;"/>
                        </div>
                        <h2 class="status-title">{{ $t('personal.verification_pending') }}</h2>
                        <p class="status-desc">{{ $t('personal.verification_pending_desc') }}</p>
                        <div class="status-tips">
                            <icon-info-circle size="16" style="color: #ff9e2d; margin-right: 8px;"/>
                            <span>{{ $t('personal.review_completion_time') }}</span>
                        </div>
                    </div>
                    <div v-else-if="authStatus === 1" class="status-approved">
                        <div class="status-icon">
                            <icon-check-circle size="64" style="color: #52c41a;"/>
                        </div>
                        <h2 class="status-title">{{ $t('personal.verification_approved') }}</h2>
                        <p class="status-desc">{{ $t('personal.verification_approved_desc') }}</p>
                        <div class="status-tips">
                            <icon-check-circle size="16" style="color: #52c41a; margin-right: 8px;"/>
                            <span>{{ $t('personal.higher_withdrawal_limit_obtained') }}</span>
                        </div>
                    </div>
                    <div v-else-if="authStatus === 2" class="status-rejected">
                        <div class="status-icon">
                            <icon-close-circle size="64" style="color: #ff4d4f;"/>
                        </div>
                        <h2 class="status-title">{{ $t('personal.verification_rejected') }}</h2>
                        <p class="status-desc">{{ $t('personal.verification_rejected_desc') }}</p>
                        <div class="reject-reason" v-if="authInfo && authInfo.reject_reason">
                            <div class="reason-box">
                                <strong>{{ $t('personal.verification_rejection_reason') }}:</strong>
                                <span>{{ authInfo.reject_reason }}</span>
                            </div>
                        </div>
                        <div class="action-buttons">
                            <a-button type="primary" size="large" @click="resetForm">
                                <template #icon>
                                    <icon-refresh />
                                </template>
                                {{ $t('personal.resubmit') }}
                            </a-button>
                        </div>
                    </div>
                </div>
            </div>
            
            <!-- 认证表单 -->
            <div v-else>
            <div class="steps">
                <a-steps :current="step">
                    <a-step :description="$t('personal.fill_basic_info')">{{ $t('personal.first_step') }}</a-step>
                    <a-step :description="$t('personal.upload_id_photo')">{{ $t('personal.second_step') }}</a-step>
                </a-steps>
            </div>
            <div class="tips">
                <icon-exclamation-polygon-fill size="22" class="color-orange"/>
                <span class="color-orange">
                    {{ $t('personal.security_notice') }}
                </span>
            </div>
            <template v-if="step === 1">
                <!--    国家或地区-->
                <div class="label">
                    <p>{{ $t('personal.country_or_region') }}</p>
                    <a-select v-model="formData.country"
                              class="input"
                              style="border-radius: 20px"
                              :placeholder="$t('personal.select_country_region')" 
                              allow-search
                              @change="onCountryChange">
                        <a-option v-for="item of area" :value="getCountryName(item)" :key="item.code" :label="getCountryName(item)"/>
                    </a-select>
                </div>
                <!--    证件类型-->
                <div class="label">
                    <p>{{ $t('personal.document_type') }}</p>
                    <a-select v-model="formData.documentType" size="large" :width="300"
                              class="input"
                              :placeholder="$t('personal.document_type')"
                              allow-clear long>
                        <a-option>{{ $t('personal.passport') }}</a-option>
                        <a-option>{{ $t('personal.driver_license') }}</a-option>
                        <a-option>{{ $t('personal.other_legal_document') }}</a-option>
                    </a-select>
                </div>
                    <!--    姓氏-->
                    <div class="label">
                        <p>{{ $t('personal.surname') }}</p>
                        <a-input v-model="formData.surname" class="input" :placeholder="$t('personal.enter_surname')"></a-input>
                    </div>
                    <!--    名字-->
                <div class="label">
                        <p>{{ $t('personal.name') }}</p>
                        <a-input v-model="formData.name" class="input" :placeholder="$t('personal.enter_name')"></a-input>
                </div>
                <!--    证件号码-->
                <div class="label">
                    <p>{{ $t('personal.document_number') }}</p>
                    <a-input v-model="formData.documentNumber" class="input" :placeholder="$t('personal.enter_real_document_number')"></a-input>
                </div>
                <a-button class="button" :disabled="!isFormValid || isSubmitting" @click="nextStep">{{ $t('personal.next_step') }}</a-button>
            </template>
            <template v-if="step === 2">
                <div class="label">
                    <p>{{ $t('personal.id_card_front') }}</p>
                        <div class="upload-container">
                            <input 
                                type="file" 
                                ref="frontFileInput" 
                                accept="image/*" 
                                style="display: none;"
                                @change="handleFrontFileSelect"
                            />
                            <div v-if="!frontFile" class="upload-button" @click="triggerFrontFileSelect">
                                <icon-upload/>
                                <p>{{ $t('personal.click_upload') }}</p>
                            </div>
                            <div v-if="frontFile" class="image-preview">
                                <img :src="frontFilePreview" :alt="$t('personal.id_card_front')" class="preview-image" />
                                <div class="preview-overlay">
                                    <a-button type="text" size="small" @click="removeFrontFile" class="remove-btn">
                                        <icon-delete />
                                    </a-button>
                                </div>
                            </div>
                        </div>
                </div>
                <div class="label">
                    <p>{{ $t('personal.id_card_back') }}</p>
                        <div class="upload-container">
                            <input 
                                type="file" 
                                ref="backFileInput" 
                                accept="image/*" 
                                style="display: none;"
                                @change="handleBackFileSelect"
                            />
                            <div v-if="!backFile" class="upload-button" @click="triggerBackFileSelect">
                                <icon-upload/>
                                <p>{{ $t('personal.click_upload') }}</p>
                            </div>
                            <div v-if="backFile" class="image-preview">
                                <img :src="backFilePreview" :alt="$t('personal.id_card_back')" class="preview-image" />
                                <div class="preview-overlay">
                                    <a-button type="text" size="small" @click="removeBackFile" class="remove-btn">
                                        <icon-delete />
                                    </a-button>
                                </div>
                            </div>
                        </div>
                </div>
                <div class="button-group">
                    <a-button class="button prev-btn" @click="prevStep" :disabled="isSubmitting">{{ $t('personal.previous_step') }}</a-button>
                    <a-button class="button" @click="submitForm" :loading="isSubmitting" :disabled="isSubmitting">
                            {{ isSubmitting ? $t('common.loading') : $t('personal.submit_verification') }}
                    </a-button>
                </div>
                <div class="tip-box">
                    <p>{{ $t('personal.upload_requirements') }}</p>
                    <p>{{ $t('personal.upload_file_format_requirement') }}</p>
                    <p>{{ $t('personal.upload_document_clarity_requirement') }}</p>
                </div>
            </template>
            </div>
        </div>
    </div>
</template>

<script>
import country from '@/common/area';
import userApi from '@/common/api/user.js';
import config from '@/common/api/config.js';

export default {
    name: 'foundationAuth',
    components: {},
    data() {
        return {
            step: 1,
            frontFile: '', // 身份证正面
            backFile: '',  // 身份证背面
            frontFilePreview: '', // 身份证正面预览URL
            backFilePreview: '',  // 身份证背面预览URL
            area: country.countryList,
            // 表单数据
            formData: {
                country: '',
                countryCode: '', // 国家代码
                dialingCode: '', // 国际区号
                documentType: '',
                surname: '', // 姓氏
                name: '', // 名字
                documentNumber: ''
            },
            // 提交状态
            isSubmitting: false,
            // 认证状态
            authStatus: null, // null=未认证, 0=审核中, 1=已通过, 2=已拒绝
            authInfo: null, // 认证信息
            isLoading: true // 加载状态
        };
    },
    mounted() {
        console.log('组件挂载，当前表单数据:', this.formData);
        // 设置默认国家（中国）
        this.setDefaultCountry();
        // 获取认证状态
        this.getAuthStatus();
    },
    computed: {
        // 检查表单是否有效
        isFormValid() {
            const countryValid = !!this.formData.country;
            const countryCodeValid = !!this.formData.countryCode;
            const dialingCodeValid = !!this.formData.dialingCode;
            const documentTypeValid = !!this.formData.documentType;
            const surnameValid = !!this.formData.surname;
            const nameValid = !!this.formData.name;
            const documentNumberValid = !!this.formData.documentNumber;
            
            const isValid = countryValid && 
                   countryCodeValid &&
                   dialingCodeValid &&
                   documentTypeValid && 
                   surnameValid && 
                   nameValid && 
                   documentNumberValid;
            
            console.log('🔍 基础认证 - 表单验证详细检查:', {
                country: this.formData.country,
                countryValid: countryValid,
                countryCode: this.formData.countryCode,
                countryCodeValid: countryCodeValid,
                dialingCode: this.formData.dialingCode,
                dialingCodeValid: dialingCodeValid,
                documentType: this.formData.documentType,
                documentTypeValid: documentTypeValid,
                surname: this.formData.surname,
                surnameValid: surnameValid,
                name: this.formData.name,
                nameValid: nameValid,
                documentNumber: this.formData.documentNumber,
                documentNumberValid: documentNumberValid,
                isValid: isValid
            });
            
            return isValid;
        }
    },
    watch: {
        // 监听语言变化
        '$i18n.locale'() {
            // 强制重新渲染组件
            this.$forceUpdate();
        }
    },
    methods: {
        goBack() {
            this.$emit("safet-event");
        },
        nextStep() {
            console.log('🔍 基础认证 - 点击下一步按钮');
            console.log('🔍 基础认证 - 当前表单数据:', this.formData);
            console.log('🔍 基础认证 - 表单验证状态:', this.isFormValid);
            
            // 使用 $nextTick 确保数据设置完成后再进行验证
            this.$nextTick(() => {
            if (this.isFormValid && !this.isSubmitting) {
                    console.log('✅ 基础认证 - 表单验证通过，进入下一步');
                this.step = 2;
                } else {
                    console.log('❌ 基础认证 - 表单验证失败，无法进入下一步');
                    // 显示具体的错误信息
                    if (!this.formData.country) {
                        this.$message.error(this.$t('personal.please_select_country'));
                    } else if (!this.formData.dialingCode) {
                        this.$message.error(this.$t('personal.dialing_code_required'));
                    } else if (!this.formData.documentType) {
                        this.$message.error(this.$t('personal.please_select_document_type'));
                    } else if (!this.formData.surname) {
                        this.$message.error(this.$t('personal.please_enter_surname'));
                    } else if (!this.formData.name) {
                        this.$message.error(this.$t('personal.please_enter_name'));
                    } else if (!this.formData.documentNumber) {
                        this.$message.error(this.$t('personal.please_enter_document_number'));
                    } else {
                        this.$message.error(this.$t('personal.please_complete_all_fields'));
                    }
                }
            });
        },
        prevStep() {
            if (!this.isSubmitting) {
                this.step = 1;
            }
        },
        onCountryChange(selectedCountryName) {
            console.log('🔍 基础认证 - 国家选择变化 - 输入的国家名称:', selectedCountryName);
            console.log('🔍 当前语言:', this.$i18n.locale);
            
            // 使用 $nextTick 确保数据正确设置
            this.$nextTick(() => {
                // 根据选择的国家名称找到对应的国家信息
                const selectedCountry = this.area.find(item => {
                    const countryName = this.getCountryName(item);
                    console.log('🔍 基础认证 - 比较:', countryName, '===', selectedCountryName, '结果:', countryName === selectedCountryName);
                    return countryName === selectedCountryName;
                });
                
                console.log('🔍 基础认证 - 找到的国家信息:', selectedCountry);
                
                if (selectedCountry) {
                    this.formData.country = selectedCountryName; // 设置国家名称
                    this.formData.countryCode = selectedCountry.code;
                    this.formData.dialingCode = selectedCountry.dialingCode;
                    console.log('✅ 基础认证 - 设置国家信息成功:', {
                        country: this.formData.country,
                        countryCode: this.formData.countryCode,
                        dialingCode: this.formData.dialingCode
                    });
                } else {
                    console.error('❌ 基础认证 - 未找到对应的国家信息:', selectedCountryName);
                    console.log('🔍 基础认证 - 所有国家列表:', this.area.map(item => ({
                        code: item.code,
                        name: this.getCountryName(item),
                        dialingCode: item.dialingCode
                    })));
                }
            });
        },
        setDefaultCountry() {
            // 设置默认国家为美国
            const defaultCountry = this.area.find(item => item.code === 'us');
            if (defaultCountry) {
                const countryName = this.getCountryName(defaultCountry);
                this.formData.country = countryName;
                this.formData.countryCode = defaultCountry.code;
                this.formData.dialingCode = defaultCountry.dialingCode;
                console.log('设置默认国家:', {
                    country: this.formData.country,
                    countryCode: this.formData.countryCode,
                    dialingCode: this.formData.dialingCode
                });
            }
        },
        async submitForm() {
            if (this.isSubmitting) {
                return; // 防止重复提交
            }
            
            // 验证区号是否设置
            if (!this.formData.dialingCode) {
                this.$message.error(this.$t('personal.please_select_country_first'));
                return;
            }
            
            // 验证是否上传了文件
            if (!this.frontFile || !this.backFile) {
                this.$message.warning(this.$t('personal.upload_requirements'));
                return;
            }
            
            this.isSubmitting = true;
            
            try {
                // 准备提交的数据 - 使用后端期望的字段名称
                const submitData = {
                    code: this.formData.dialingCode, // 国际区号
                    surname: this.formData.surname, // 姓氏
                    name: this.formData.name, // 名字
                    no: this.formData.documentNumber, // 证件号码
                    type: this.getDocumentTypeCode(this.formData.documentType), // 证件类型代码
                    // 上传的文件
                    img1: this.frontFile ? this.frontFile.url : '', // 身份证正面
                    img2: this.backFile ? this.backFile.url : '' // 身份证反面
                };
                
                console.log('🔍 基础认证 - 姓名处理:', {
                    surname: this.formData.surname,
                    name: this.formData.name
                });
                
                console.log('提交认证数据:', submitData);
                console.log('表单验证状态:', this.isFormValid);
                console.log('当前表单数据:', this.formData);
                
                // 调用API提交数据
                const response = await userApi.submitAuthInfo(submitData);
                
                if (response && response.code === 200) {
                    this.$message.success(this.$t('personal.verification_submit_success'));
                    // 提交成功后返回身份认证页面
                    setTimeout(() => {
                        this.goBack();
                    }, 1500);
                } else {
                    this.$message.error(response?.message || this.$t('common.failed'));
                }
            } catch (error) {
                console.error('提交认证失败:', error);
                this.$message.error(this.$t('common.failed'));
            } finally {
                this.isSubmitting = false;
            }
        },
        async getAuthStatus() {
            try {
                console.log('🔍 获取认证状态...');
                const response = await userApi.baseAuthInfo();
                
                if (response && response.code === 200) {
                    this.authInfo = response.data;
                    this.authStatus = response.data.status || null;
                    
                    console.log('✅ 认证状态获取成功:', {
                        status: this.authStatus,
                        info: this.authInfo
                    });
                    
                    // 如果已认证，填充表单数据
                    if (this.authInfo && this.authInfo.status) {
                        this.fillFormData();
                    }
                } else {
                    console.log('ℹ️ 未找到认证信息，可能是首次认证');
                    this.authStatus = null;
                    this.authInfo = null;
                }
            } catch (error) {
                console.error('❌ 获取认证状态失败:', error);
                this.authStatus = null;
                this.authInfo = null;
            } finally {
                this.isLoading = false;
            }
        },
        fillFormData() {
            // 根据认证信息填充表单数据
            if (this.authInfo) {
                // 从认证信息中提取数据
                this.formData.country = this.authInfo.country || '';
                this.formData.countryCode = this.authInfo.country_code || '';
                this.formData.dialingCode = this.authInfo.code || '';
                this.formData.surname = this.authInfo.surname || '';
                this.formData.name = this.authInfo.name || '';
                this.formData.documentNumber = this.authInfo.no || '';
                
                // 根据type设置证件类型
                const documentType = this.getDocumentTypeText(this.authInfo.type);
                this.formData.documentType = documentType;
                
                console.log('✅ 表单数据已填充:', this.formData);
            }
        },
        getDocumentTypeText(type) {
            // 根据证件类型代码获取文本
            switch (type) {
                case '1':
                    return this.$t('personal.passport');
                case '2':
                    return this.$t('personal.driver_license');
                case '3':
                    return this.$t('personal.other_legal_document');
                default:
                    return this.$t('personal.passport');
            }
        },
        triggerFrontFileSelect() {
            this.$refs.frontFileInput.click();
        },
        triggerBackFileSelect() {
            this.$refs.backFileInput.click();
        },
        removeFrontFile() {
            this.frontFile = '';
            this.frontFilePreview = '';
            // 清除input的值
            if (this.$refs.frontFileInput) {
                this.$refs.frontFileInput.value = '';
            }
        },
        removeBackFile() {
            this.backFile = '';
            this.backFilePreview = '';
            // 清除input的值
            if (this.$refs.backFileInput) {
                this.$refs.backFileInput.value = '';
            }
        },
        async handleFrontFileSelect(event) {
            const selectedFile = event.target.files[0];
            if (!selectedFile) {
                return;
            }
            
            console.log('📤 选择的正面文件:', selectedFile);
            
            // 检查文件大小
            if (selectedFile.size > 5 * 1024 * 1024) {
                this.$message.error(this.$t('personal.file_size_exceeded'));
                return;
            }
            
            // 检查文件类型
            if (!selectedFile.type.startsWith('image/')) {
                this.$message.error(this.$t('personal.please_select_image'));
                return;
            }
            
            // 创建预览URL
            this.frontFilePreview = URL.createObjectURL(selectedFile);
            
            try {
                // 创建FormData
                const formData = new FormData();
                formData.append('file', selectedFile);
                formData.append('type', 'image');
                
                // 获取认证信息
                const token = this.$util.getCache('token', '');
                const lang = this.$util.getCache('lang', 'en-US');
                
                // 发送上传请求
                const response = await fetch(`${config.BASE_URL}/api/upload/image`, {
                    method: 'POST',
                    headers: {
                        'Authorization': 'Bearer ' + token,
                        'Accept-Language': lang,
                        'Client-Device': '0'
                    },
                    body: formData
                });
                
                if (!response.ok) {
                    throw new Error(`HTTP error! status: ${response.status}`);
                }
                
                const result = await response.json();
                
                if (result.code === 200) {
                    console.log('✅ 正面照片上传成功:', result);
                    // 设置文件信息
                    this.frontFile = {
                        uid: Date.now(),
                        name: selectedFile.name,
                        url: result.data?.url || result.url,
                        status: 'done'
                    };
                    this.$message.success(this.$t('personal.front_photo_upload_success'));
                } else {
                    console.error('❌ 正面照片上传失败:', result);
                    this.$message.error(result.message || this.$t('personal.upload_failed'));
                    // 清除预览
                    this.frontFilePreview = '';
                }
            } catch (error) {
                console.error('❌ 正面照片上传错误:', error);
                this.$message.error(this.$t('personal.upload_failed_retry'));
                // 清除预览
                this.frontFilePreview = '';
            }
        },
        async handleBackFileSelect(event) {
            const selectedFile = event.target.files[0];
            if (!selectedFile) {
                return;
            }
            
            console.log('📤 选择的背面文件:', selectedFile);
            
            // 检查文件大小
            if (selectedFile.size > 5 * 1024 * 1024) {
                this.$message.error(this.$t('personal.file_size_exceeded'));
                return;
            }
            
            // 检查文件类型
            if (!selectedFile.type.startsWith('image/')) {
                this.$message.error(this.$t('personal.please_select_image'));
                return;
            }
            
            // 创建预览URL
            this.backFilePreview = URL.createObjectURL(selectedFile);
            
            try {
                // 创建FormData
                const formData = new FormData();
                formData.append('file', selectedFile);
                formData.append('type', 'image');
                
                // 获取认证信息
                const token = this.$util.getCache('token', '');
                const lang = this.$util.getCache('lang', 'en-US');
                
                // 发送上传请求
                const response = await fetch(`${config.BASE_URL}/api/upload/image`, {
                    method: 'POST',
                    headers: {
                        'Authorization': 'Bearer ' + token,
                        'Accept-Language': lang,
                        'Client-Device': '0'
                    },
                    body: formData
                });
                
                if (!response.ok) {
                    throw new Error(`HTTP error! status: ${response.status}`);
                }
                
                const result = await response.json();
                
                if (result.code === 200) {
                    console.log('✅ 背面照片上传成功:', result);
                    // 设置文件信息
                    this.backFile = {
                        uid: Date.now(),
                        name: selectedFile.name,
                        url: result.data?.url || result.url,
                        status: 'done'
                    };
                    this.$message.success(this.$t('personal.back_photo_upload_success'));
                } else {
                    console.error('❌ 背面照片上传失败:', result);
                    this.$message.error(result.message || this.$t('personal.upload_failed'));
                    // 清除预览
                    this.backFilePreview = '';
                }
            } catch (error) {
                console.error('❌ 背面照片上传错误:', error);
                this.$message.error(this.$t('personal.upload_failed_retry'));
                // 清除预览
                this.backFilePreview = '';
            }
        },
        async beforeFrontUpload(file) {
            console.log('📤 beforeFrontUpload 接收到的文件:', file);
            
            try {
                // 检查文件是否存在
                if (!file) {
                    throw new Error('文件对象为空');
                }
                
                // 创建FormData
                const formData = new FormData();
                formData.append('file', file);
                formData.append('type', 'image');
                
                // 添加调试信息
                console.log('📁 文件信息:', {
                    name: file?.name || '未知文件名',
                    size: file?.size || 0,
                    type: file?.type || '未知类型',
                    lastModified: file?.lastModified || 0
                });
                
                // 获取认证信息
                const token = this.$util.getCache('token', '');
                const lang = this.$util.getCache('lang', 'en-US');
                
                // 发送上传请求
                const response = await fetch(`${config.BASE_URL}/api/upload/image`, {
                    method: 'POST',
                    headers: {
                        'Authorization': 'Bearer ' + token,
                        'Accept-Language': lang,
                        'Client-Device': '0'
                    },
                    body: formData
                });
                
                if (!response.ok) {
                    throw new Error(`HTTP error! status: ${response.status}`);
                }
                
                const result = await response.json();
                
                if (result.code === 200) {
                    console.log('✅ 正面照片上传成功:', result);
                    // 设置文件信息
                    this.frontFile = {
                        uid: file.uid || Date.now(),
                        name: file.name,
                        url: result.data?.url || result.url,
                        status: 'done'
                    };
                    return false; // 阻止默认上传行为
                } else {
                    console.error('❌ 正面照片上传失败:', result);
                    this.$message.error(result.message || this.$t('personal.upload_failed'));
                    return false;
                }
            } catch (error) {
                console.error('❌ 正面照片上传错误:', error);
                this.$message.error(this.$t('personal.upload_failed_retry'));
                return false;
            }
        },
        async beforeBackUpload(file) {
            console.log('📤 beforeBackUpload 接收到的文件:', file);
            
            try {
                // 检查文件是否存在
                if (!file) {
                    throw new Error('文件对象为空');
                }
                
                // 创建FormData
                const formData = new FormData();
                formData.append('file', file);
                formData.append('type', 'image');
                
                // 添加调试信息
                console.log('📁 文件信息:', {
                    name: file?.name || '未知文件名',
                    size: file?.size || 0,
                    type: file?.type || '未知类型',
                    lastModified: file?.lastModified || 0
                });
                
                // 获取认证信息
                const token = this.$util.getCache('token', '');
                const lang = this.$util.getCache('lang', 'en-US');
                
                // 发送上传请求
                const response = await fetch(`${config.BASE_URL}/api/upload/image`, {
                    method: 'POST',
                    headers: {
                        'Authorization': 'Bearer ' + token,
                        'Accept-Language': lang,
                        'Client-Device': '0'
                    },
                    body: formData
                });
                
                if (!response.ok) {
                    throw new Error(`HTTP error! status: ${response.status}`);
                }
                
                const result = await response.json();
                
                if (result.code === 200) {
                    console.log('✅ 背面照片上传成功:', result);
                    // 设置文件信息
                    this.backFile = {
                        uid: file.uid || Date.now(),
                        name: file.name,
                        url: result.data?.url || result.url,
                        status: 'done'
                    };
                    return false; // 阻止默认上传行为
                } else {
                    console.error('❌ 背面照片上传失败:', result);
                    this.$message.error(result.message || this.$t('personal.upload_failed'));
                    return false;
                }
            } catch (error) {
                console.error('❌ 背面照片上传错误:', error);
                this.$message.error(this.$t('personal.upload_failed_retry'));
                return false;
            }
        },
        async customUpload(options) {
            console.log('📤 customUpload 接收到的参数:', options);
            
            // 检查options参数
            if (!options) {
                throw new Error('customUpload 参数为空');
            }
            
            // 尝试不同的参数解构方式
            const file = options.file || options.data?.file || options;
            const onProgress = options.onProgress || (() => {});
            const onSuccess = options.onSuccess || (() => {});
            const onError = options.onError || (() => {});
            
            try {
                console.log('📤 开始上传文件:', file);
                
                // 检查文件是否存在
                if (!file) {
                    console.error('❌ 文件对象为空，options:', options);
                    throw new Error('文件对象为空');
                }
                
                // 创建FormData
                const formData = new FormData();
                formData.append('file', file); // 使用标准的'file'字段名
                formData.append('type', 'image'); // 添加文件类型标识
                
                // 添加调试信息
                console.log('📁 文件信息:', {
                    name: file?.name || '未知文件名',
                    size: file?.size || 0,
                    type: file?.type || '未知类型',
                    lastModified: file?.lastModified || 0
                });
                
                // 获取认证信息
                const token = this.$util.getCache('token', '');
                const lang = this.$util.getCache('lang', 'en-US');
                
                // 发送上传请求
                const response = await fetch(`${config.BASE_URL}/api/upload/image`, {
                    method: 'POST',
                    headers: {
                        'Authorization': 'Bearer ' + token,
                        'Accept-Language': lang,
                        'Client-Device': '0'
                        // 注意：不要设置Content-Type，让浏览器自动设置multipart/form-data
                    },
                    body: formData
                });
                
                if (!response.ok) {
                    throw new Error(`HTTP error! status: ${response.status}`);
                }
                
                const result = await response.json();
                
                if (result.code === 200) {
                    console.log('✅ 文件上传成功:', result);
                    onSuccess(result);
                } else {
                    console.error('❌ 文件上传失败:', result);
                    onError(new Error(result.message || '上传失败'));
                }
            } catch (error) {
                console.error('❌ 文件上传错误:', error);
                onError(error);
            }
        },
        onFrontFileChange(info) {
            console.log('📁 正面文件变化:', info);
            
            // 检查info和info.file是否存在
            if (!info || !info.file) {
                console.log('ℹ️ 正面文件信息不完整:', info);
                return;
            }
            
            if (info.file.status === 'done') {
                // 上传成功
            this.frontFile = {
                    uid: info.file.uid,
                    name: info.file.name,
                    url: info.file.response?.data?.url || info.file.url,
                    status: 'done'
                };
                console.log('✅ 正面文件上传完成:', this.frontFile);
            } else if (info.file.status === 'error') {
                // 上传失败
                console.error('❌ 正面文件上传失败:', info.file);
                this.$message.error(this.$t('personal.front_file_upload_failed'));
            } else {
                // 其他状态（如uploading等）
                console.log('📤 正面文件上传状态:', info.file.status);
            }
        },
        onBackFileChange(info) {
            console.log('📁 背面文件变化:', info);
            
            // 检查info和info.file是否存在
            if (!info || !info.file) {
                console.log('ℹ️ 背面文件信息不完整:', info);
                return;
            }
            
            if (info.file.status === 'done') {
                // 上传成功
            this.backFile = {
                    uid: info.file.uid,
                    name: info.file.name,
                    url: info.file.response?.data?.url || info.file.url,
                    status: 'done'
                };
                console.log('✅ 背面文件上传完成:', this.backFile);
            } else if (info.file.status === 'error') {
                // 上传失败
                console.error('❌ 背面文件上传失败:', info.file);
                this.$message.error(this.$t('personal.back_file_upload_failed'));
            } else {
                // 其他状态（如uploading等）
                console.log('📤 背面文件上传状态:', info.file.status);
            }
        },
        onProgress(currentFile) {
            // 保持原有的进度处理方法
        },
        getCountryName(item) {
            // 根据当前语言返回对应的国家名称
            const locale = this.$i18n.locale;
            
            // 尝试从多语言文件中获取国家名称
            const countryKey = `countries.${item.code}`;
            const translatedName = this.$t(countryKey);
            
            // 如果翻译存在且不是键名本身，则使用翻译
            if (translatedName && translatedName !== countryKey) {
                return translatedName;
            }
            
            // 否则使用默认的英文或中文名称
            if (locale === 'en-US' || locale === 'au') {
                return item.enName;
            } else if (locale === 'zh_CN' || locale === 'zh_Hant') {
                return item.cnName;
            } else {
                // 对于其他语言，暂时使用英文名称
                return item.enName;
            }
        },
        getDocumentTypeCode(type) {
            // 处理多语言翻译
            const passportText = this.$t('personal.passport');
            const driverLicenseText = this.$t('personal.driver_license');
            const otherDocumentText = this.$t('personal.other_legal_document');
            
            switch (type) {
                case passportText:
                    return '1';
                case driverLicenseText:
                    return '2';
                case otherDocumentText:
                    return '3';
                default:
                    return '1'; // 默认护照
            }
        },
        resetForm() {
            // 重置表单数据
            this.formData = {
                country: '',
                countryCode: '',
                dialingCode: '',
                documentType: '',
                surname: '',
                name: '',
                documentNumber: ''
            };
            this.step = 1;
            this.frontFile = '';
            this.backFile = '';
            this.authStatus = null;
            this.authInfo = null;
            this.isSubmitting = false;
            
            // 重新设置默认国家
            this.setDefaultCountry();
            
            console.log('✅ 表单已重置');
        }

    },
};
</script>
<style lang="scss" scoped>
@import "./foundationAuth.scss";
</style>
